import { InboxOutlined, PrinterFilled } from "@ant-design/icons"
import PhoneInput from "antd-phone-input"
import { Button } from "components/Button/Button"
import { Checkbox } from "components/Checkbox/Checkbox"
import { Form, type FormRule, type NamePath } from "components/Form/Form"
import { Input } from "components/Input/Input"
import { Radio } from "components/Radio/Radio"
import { Select } from "components/Select/Select"
import { Upload, type UploadFile } from "components/Upload/Upload"

import { ErrorCodeEnum } from "api/api"
import { BackButton } from "components/BackButton/BackButton"
import { DatePicker } from "components/DatePicker/DatePicker"
import { Drawer, type DrawerProps } from "components/Drawer/Drawer"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import {
	Enrollment,
	InsuranceKeys,
	INSURANCES,
	UseMutationResultEnrollment,
} from "interface/enrollment"
import { AlternateContact, BIOLOGICAL_SEXES } from "interface/user"
import { useFlag } from "modules/flags/flags"
import { AlternateContactsFormItem } from "modules/patient/AlternateContactsFormItem"
import {
	normalizePhoneNumber,
	ruleUniquePhoneNumber,
	ruleValidContactInfo,
	useCheckPhoneNumberAsync,
} from "modules/patient/contact-info"
import { useMemo, useRef } from "react"
import { useTemplateURL } from "services/enrollments"
import { asDate, today, type Dayjs } from "utils/date"
import { downloadFileFromUrl } from "utils/file"
import { v4 as uuidv4 } from "uuid"

export const EnrollForm = ({
	enrollment,
	title = "Enroll patient",
	backButtonLabel = "Pending enrollments",
	submitButtonLabel = "Request enrollment for patient",
	isPending,
	isError,
	rootClassName,
	mutate,
	onClose,
	onSuccess,
	...props
}: DrawerProps &
	Partial<UseMutationResultEnrollment> & {
		enrollment?: Enrollment
		backButtonLabel?: string
		submitButtonLabel?: string
		onSuccess: VoidFunction
	}) => {
	const { patientBrochure } = useFlag("patientBrochure")
	const { data: consentFormTemplateURL } = useTemplateURL(
		"consent-form-template",
	)
	const { data: patientBrochureURL } = useTemplateURL(
		"patient-brochure",
		patientBrochure.enabled,
	)
	// When editing, the enrollment's own numbers must not count as duplicates.
	const { ruleCheckPhoneNumberAsync } = useCheckPhoneNumberAsync(
		undefined,
		enrollment?.id,
	)
	const [form] = Form.useForm<FormValues>()
	const alternateContacts =
		Form.useWatch<AlternateContact[]>("alternateContacts", form) ?? []
	const { current: formName } = useRef(`enroll-patient-${uuidv4()}`)
	const initialValues = useMemo<FormValues | undefined>(
		() =>
			enrollment
				? {
						address: enrollment.address,
						fullName: enrollment.fullName,
						email: enrollment.email,
						biologicalSex: enrollment.biologicalSex,
						medicalRecordNumber: enrollment.medicalRecordNumber,
						lastRecordedWeightPounds: enrollment.lastRecordedWeightPounds,
						notes: enrollment.notes,
						alternateContacts: enrollment.alternateContacts,
						phoneNumber: enrollment.phoneNumber?.value ?? "",
						supportsTextMessages: enrollment.phoneNumber
							? enrollment.phoneNumber.supportsTextMessages
							: false,
						consentForm: [
							{
								uid: uuidv4(),
								name: `signed-consent-form-${enrollment.fullName}`,
								status: "done",
								url: enrollment.consentFormUrl!,
							},
						],
						dateOfBirth: asDate(enrollment.dateOfBirth),
						insurances: enrollment.insurances?.map(
							({ custom, name }) => custom ?? name,
						) as InsuranceKeys[],
					}
				: undefined,
		[enrollment],
	)
	const onFinish = (formValues: FormValues) => {
		const {
			consentForm,
			dateOfBirth,
			phoneNumber,
			supportsTextMessages,
			email,
			lastRecordedWeightPounds,
			...values
		} = formValues
		const insurances = values.insurances.map(
			(insurance) =>
				INSURANCES[insurance] ?? { name: "Other", custom: insurance },
		)

		mutate?.(
			{
				...values,
				insurances,
				dateOfBirth: dateOfBirth.toISOString(),
				// Editing keeps the stored file unless a new one is dropped in, in
				// which case it carries an `originFileObj`.
				files: consentForm[0]?.originFileObj as File | undefined,
				email: email && email.length > 0 ? email : undefined,
				lastRecordedWeightPounds:
					lastRecordedWeightPounds != null &&
					String(lastRecordedWeightPounds).trim().length > 0
						? Number(lastRecordedWeightPounds)
						: undefined,
				phoneNumber: {
					value: phoneNumber ?? "",
					supportsTextMessages,
				},
			},
			{
				onSuccess,
				onError: (error) => {
					type FieldPath =
						typeof fieldPaths.phoneNumberValue | typeof fieldPaths.email
					const fieldMap: Partial<Record<ErrorCodeEnum, FieldPath>> = {
						[ErrorCodeEnum.InvalidPhoneNumber]: fieldPaths.phoneNumberValue,
						[ErrorCodeEnum.PhoneNumberAlreadyExistsError]:
							fieldPaths.phoneNumberValue,
						[ErrorCodeEnum.EmailAlreadyExistsError]: fieldPaths.email,
					}
					const fieldPath = fieldMap[error.code as ErrorCodeEnum]
					if (fieldPath && error.message) {
						form.setFields([{ name: fieldPath, errors: [error.message] }])
						form.scrollToField(fieldPath, { behavior: "smooth" })
					}
				},
			},
		)
	}

	return (
		<Drawer
			{...props}
			onClose={onClose}
			title={<BackButton onClick={onClose}>{backButtonLabel}</BackButton>}
			aria-label={title as string}
			rootClassName={rootClassName}
		>
			<h2 className="font-headings text-3xl font-semibold">{title}</h2>

			{(consentFormTemplateURL || patientBrochureURL) && (
				<div className="flex justify-start gap-4">
					{consentFormTemplateURL && (
						<Button
							icon={<PrinterFilled aria-hidden />}
							onClick={async () => {
								await downloadFileFromUrl(
									consentFormTemplateURL,
									"consent-form-template",
								)
							}}
						>
							Print consent form
						</Button>
					)}
					{patientBrochureURL && (
						<Button
							icon={<PrinterFilled aria-hidden />}
							onClick={async () => {
								await downloadFileFromUrl(
									patientBrochureURL,
									"patient-brochure",
								)
							}}
						>
							Print patient brochure
						</Button>
					)}
				</div>
			)}
			<Form<FormValues>
				form={form}
				layout="vertical"
				autoComplete="off"
				name={formName}
				aria-label={title as string}
				className="overflow-y-auto rounded-2xl bg-mint-0 p-2 xs:p-4"
				initialValues={initialValues}
				onFinish={onFinish}
			>
				<p className="mb-4 text-navy-60">
					Please use the same <b>address</b>, <b>email</b>, and{" "}
					<b>phone number</b> from the consent
				</p>
				<Form.Item
					name="fullName"
					label="Full name"
					rules={[
						...baseRules,
						{ whitespace: true },
						{
							pattern: /^[A-Za-z\s.'-]+$/,
							message: "Please enter only letters, spaces, hyphens, and dots",
						},
						{
							max: 100,
							message: "Full name cannot exceed 100 characters",
						},
					]}
				>
					<Input />
				</Form.Item>
				<Form.Item
					name="address"
					label="Address"
					rules={[
						...baseRules,
						{ whitespace: true },
						{
							pattern: /^[a-zA-Z0-9\s,.'-]*$/,
							message: "Address cannot contain invalid characters",
						},
						{
							max: 300,
							message: "Address cannot exceed 300 characters",
						},
					]}
				>
					<Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
				</Form.Item>
				<Form.Item name="dateOfBirth" label="Date of birth" rules={baseRules}>
					<DatePicker
						className="w-full"
						format={{ format: "MM/DD/YYYY", type: "mask" }}
						placeholder="MM/DD/YYYY"
						maxDate={today().subtract(1, "day")}
					/>
				</Form.Item>
				<Form.Item
					name="biologicalSex"
					label="Biological sex"
					rules={baseRules}
				>
					<Radio.Group
						options={BIOLOGICAL_SEXES.map((sex) => ({
							value: sex,
							label: sex,
						}))}
					/>
				</Form.Item>
				<Form.Item label="Phone number">
					<div className="flex flex-col gap-1">
						<Form.Item
							noStyle
							name="phoneNumber"
							dependencies={[
								"email",
								"supportsTextMessages",
								...alternateContacts.map((_, index) => [
									"alternateContacts",
									index,
									"phoneNumber",
									"value",
								]),
							]}
							normalize={normalizePhoneNumber}
							validateFirst
							validateDebounce={300}
							validateTrigger="onBlur"
							rules={[
								ruleValidContactInfo(),
								ruleUniquePhoneNumber(),
								ruleCheckPhoneNumberAsync,
							]}
						>
							<PhoneInput aria-label="Patient's phone number" />
						</Form.Item>
						<Form.Item
							noStyle
							name="supportsTextMessages"
							valuePropName="checked"
							dependencies={["phoneNumber", "email"]}
						>
							<Checkbox aria-label="Patient's phone supports text messages">
								Supports text messages
							</Checkbox>
						</Form.Item>
					</div>
				</Form.Item>
				<Form.Item
					name="email"
					label="Email"
					rules={[ruleValidContactInfo(), { type: "email" }]}
					dependencies={["phoneNumber", "supportsTextMessages"]}
				>
					<Input />
				</Form.Item>

				<Form.Item
					name="medicalRecordNumber"
					label="Medical record number"
					rules={[
						...baseRules,
						{ whitespace: true },
						{
							pattern: /^[a-zA-Z0-9\s-]*$/,
							message:
								"Medical record number only allows letters, numbers, and hyphens",
						},
						{
							max: 50,
							message: "Medical record number cannot exceed 50 characters",
						},
					]}
				>
					<Input />
				</Form.Item>
				<Form.Item
					name="lastRecordedWeightPounds"
					label="Last recorded weight"
					rules={[
						{
							type: "number",
							min: 0,
							max: 999,
							transform(value) {
								return value ? Number(value) : undefined
							},
						},
					]}
				>
					<Input type="number" suffix="lbs" />
				</Form.Item>
				<Form.Item name="insurances" label="Insurance" rules={baseRules}>
					<Select
						mode="tags"
						virtual={false}
						placeholder="Select one or more insurances or input a new one"
						options={Object.keys(INSURANCES).map((insurance) => ({
							value: insurance,
							label: insurance,
						}))}
					/>
				</Form.Item>
				<Form.Item label="Signed consent form" required>
					<Form.Item
						noStyle
						name="consentForm"
						label="Signed consent form"
						valuePropName="fileList"
						rules={[
							{
								required: true,
								message: "Please upload a PDF file",
							},
						]}
						getValueFromEvent={({
							file,
							fileList,
						}: {
							file: UploadFile
							fileList: UploadFile[]
						}) => {
							if (
								file.type !== "application/pdf" ||
								!checkFileSize(file.size ?? 0)
							) {
								return null
							}
							return fileList
						}}
					>
						<Upload.Dragger
							maxCount={1}
							name="files"
							accept="application/pdf"
							data-testid={"Signed consent form"}
							beforeUpload={(file) => {
								if (checkFileSize(file.size)) {
									return false
								} else {
									void form.setFields([
										{
											name: "consentForm",
											value: [],
											errors: [
												"The uploaded file exceeds the maximum allowed size of 5 MB",
											],
										},
									])
									return Upload.LIST_IGNORE
								}
							}}
						>
							<InboxOutlined aria-hidden className="text-4xl text-green-60" />
							Click or drag the PDF file here to upload
						</Upload.Dragger>
					</Form.Item>
				</Form.Item>
				<Form.Item<FormValues>
					name="notes"
					label="Enrollment notes"
					rules={[
						{
							max: 300,
							message: "The notes cannot exceed 300 characters",
						},
					]}
				>
					<Input.TextArea
						placeholder="Enter any notes that Empo Health should know when enrolling this patient"
						autoSize={{ minRows: 2, maxRows: 5 }}
					/>
				</Form.Item>
				<AlternateContactsFormItem
					formName={formName}
					permission="edit"
					patientPhoneNumberPath="phoneNumber"
					enrollmentId={enrollment?.id}
				/>
				<Form.Item className="pt-2">
					<div className="flex flex-col gap-2">
						<Button
							type="primary"
							htmlType="button"
							loading={isPending}
							className="w-full"
							onClick={form.submit}
						>
							{submitButtonLabel}
						</Button>
						<ErrorStatus show={isError}>
							An unexpected error occurred. Please, try again.
						</ErrorStatus>
					</div>
				</Form.Item>
			</Form>
		</Drawer>
	)
}

type FormValues = Pick<
	Enrollment,
	| "address"
	| "fullName"
	| "email"
	| "biologicalSex"
	| "medicalRecordNumber"
	| "lastRecordedWeightPounds"
	| "notes"
	| "alternateContacts"
> & {
	phoneNumber: string
	supportsTextMessages: boolean
	consentForm: UploadFile[]
	dateOfBirth: Dayjs
	insurances: InsuranceKeys[]
}

const fieldPaths: Record<string, NamePath<FormValues>> = {
	phoneNumberValue: ["phoneNumber"],
	phoneNumberSupports: ["supportsTextMessages"],
	email: ["email"],
	alternateContacts: ["alternateContacts"],
}

const baseRules: FormRule[] = [{ required: true }]

const checkFileSize = (
	sizeInBytes: number,
	maxSizeInBytes: number = 5_000_000,
) => sizeInBytes <= maxSizeInBytes
